import { getSession } from '../auth';
import { toast } from '../components/toast';
import { addStudentToBatch, createBatch, isProductionData, listBatches, listCourses, listStudentsForBatch } from '../lib/repository';
import { setSelectedBatchId, getSelectedBatchId } from '../lib/batchContext';
import { openModal, closeModal } from '../components/modal';
import type { CourseRecord, BatchRecord } from '../domain';

let selectedBatch = '';
let productionBatches: BatchRecord[] | null = null;
let productionBatchesLoaded = false;
let productionBatchesLoading = false;
let courseCache: CourseRecord[] = [];
let coursesLoaded = false;
let rosters: Record<string, Awaited<ReturnType<typeof listStudentsForBatch>>> = {};

async function refreshBatches(force: boolean = false) {
  if (!isProductionData() || productionBatchesLoading) return;
  if (productionBatchesLoaded && !force) return;
  productionBatchesLoading = true;
  try { productionBatches = await listBatches(); }
  catch { productionBatches = []; }
  productionBatchesLoading = false;
  productionBatchesLoaded = true;
  (window as any).__rerender?.();
}
async function refreshCourses() {
  if (coursesLoaded || !isProductionData()) return;
  try { courseCache = await listCourses(); } catch { courseCache = []; }
  coursesLoaded = true;
}

export function renderBatches(): string {
  void refreshBatches();
  void refreshCourses();
  const session = getSession();
  const canManage = !!session && (session.role === 'owner' || session.role === 'admin');
  const visible = productionBatches ?? [];
  if (!selectedBatch && visible.length) selectedBatch = visible[0].id;
  const selected = visible.find((b) => b.id === selectedBatch) || visible[0];
  const course = courseCache.find((c) => c.id === selected?.courseId);
  // State machine: once we've fetched at least once, treat any re-render as
  // ready/empty (no loading flicker). Only the FIRST mount shows loading.
  type State = 'loading' | 'empty' | 'ready';
  const state: State = !productionBatchesLoaded
    ? 'loading'
    : visible.length === 0 ? 'empty' : 'ready';

  const headerHTML = (() => {
    if (state === 'loading') {
      return '<h1 class="t-h1">Loading batches…</h1><p class="t-meta">Fetching from your tenant database</p>';
    }
    if (state === 'empty') {
      return '<h1 class="t-h1">No batches yet</h1><p class="t-meta">Create your first batch to start scheduling classes and tracking attendance.</p>';
    }
    return '<h1 class="t-h1">' + (selected ? selected.name : 'Batches') + '</h1><p class="t-meta">' + visible.length + ' ' + (visible.length === 1 ? 'batch' : 'batches') + '</p>';
  })();

  const batchCards = visible.map((b) => {
    const c = courseCache.find((x) => x.id === b.courseId);
    return '<button class="card card-pad-sm batch-card ' + (b.id === selected?.id ? 'active' : '') + '" data-batch-select="' + b.id + '" style="width:100%;text-align:left;cursor:pointer;border:1px solid ' + (b.id === selected?.id ? 'var(--accent)' : 'var(--rule)') + '">' +
      '<div class="row"><span class="pill ' + (b.status === 'live' ? 'pill-success' : 'pill-info') + '">' + b.status + '</span><span class="spacer"></span><span class="t-mono t-meta">' + (b.startDate ? b.startDate.slice(5) : '—') + '</span></div>' +
      '<div class="t-strong" style="margin-top:8px">' + b.name + '</div><div class="t-meta">' + ((c && c.title) || 'Course') + ' · ' + (b.schedule || '—') + '</div></button>';
  }).join('');

  let body = '';
  if (state === 'loading') {
    body = '<div class="batch-layout stagger"><aside class="stack-tight"><div class="empty" style="padding:24px 0;text-align:center"><div class="t-meta">Loading…</div></div></aside><section class="stack"><div class="card card-pad-lg"><div class="attendance-empty"><h3>Loading roster…</h3><p>Fetching your tenant\'s batches.</p></div></div></section></div>';
  } else if (state === 'empty') {
    body = '<div class="batch-layout stagger"><aside class="stack-tight"></aside><section class="stack"><div class="card card-pad-lg"><div class="attendance-empty"><h3>No batches yet</h3><p>Click <strong>+ New batch</strong> to create your first batch and start managing cohorts.</p>' + (canManage ? '<button class="btn btn-primary" data-action="batch-new">+ New batch</button>' : '') + '</div></div></section></div>';
  } else {
    let facts = '';
    if (!selected) {
      facts = '<div class="attendance-empty"><h3>Pick a batch</h3><p>Select a batch from the list on the left to view its details.</p></div>';
    } else {
      facts = '<div class="batch-facts">' +
        '<div><span>START</span><strong>' + (selected.startDate || '—') + '</strong></div>' +
        '<div><span>END</span><strong>' + (selected.endDate || '—') + '</strong></div>' +
        '<div><span>TUTOR</span><strong>' + (selected.tutorUid || '—') + '</strong></div>' +
        '<div><span>FINAL TEST</span><strong>' + (selected.finalTestDate || '—') + '</strong></div>' +
      '</div><div class="t-meta" style="margin-top:10px">Schedule · ' + (selected.schedule || '—') + '</div>';
    }
    const roster = selected ? (rosters[selected.id] || []) : [];
    const rosterHTML = roster.length
      ? roster.map((s) => '<div class="row" style="padding:6px 0;border-bottom:1px solid var(--rule)"><div class="avatar av-c2">' + (s.name || '?').slice(0, 2).toUpperCase() + '</div><div style="flex:1"><div class="t-strong">' + s.name + '</div><div class="t-meta t-mono">' + (s.email || '') + '</div></div></div>').join('')
      : '<div class="empty" style="padding:16px 0">No learners in this batch yet.</div>';
    body = '<div class="batch-layout stagger"><aside class="stack-tight">' + batchCards + '</aside><section class="stack">' +
      '<div class="card card-pad-lg"><div class="row"><div style="flex:1"><div class="t-eyebrow">Selected batch</div><h2 class="t-h2" style="margin-top:6px">' + (selected?.name || '—') + '</h2><div class="t-meta">' + ((course && course.title) || 'Course') + ' · ' + (selected?.status || '') + '</div></div></div>' + facts +
      '<div class="card" style="margin-top:16px"><div class="card-head"><div><div class="t-eyebrow">Students in this batch</div><div class="t-h2" style="margin-top:4px">Progress &amp; attendance</div></div>' +
      (canManage && selected ? '<button class="btn btn-sm btn-ghost" data-action="add-student-batch">+ Add learner</button>' : '') + '</div>' +
      '<div class="t-meta" style="padding:8px 0">' + rosterHTML + '</div></div></div></section></div>';
  }

  return (
    '<div class="screen-head"><div>' + headerHTML + '</div>' +
    (state !== 'loading' && canManage ? '<button class="btn btn-accent touch-target" data-action="batch-new">+ New batch</button>' : '') + '</div>' + body
  );
}

export function bindBatches(): void {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const card = t.closest<HTMLElement>('[data-batch-select]');
    if (card && card.dataset.batchSelect) {
      selectedBatch = card.dataset.batchSelect;
      setSelectedBatchId(selectedBatch);
      (window as any).__rerender?.();
      void listStudentsForBatch(selectedBatch).then((rs) => { rosters[selectedBatch] = rs; (window as any).__rerender?.(); }).catch(() => { rosters[selectedBatch] = []; });
      return;
    }
    if (t.closest('[data-action="add-student-batch"]')) { openAddStudentForm(); return; }
    if (t.closest('[data-action="batch-new"]')) { openBatchForm(); return; }
    if (t.closest('[data-batch-close]')) { closeModal('batch-create'); closeModal('student-batch-add'); }
  });

  document.addEventListener('submit', async (e) => {
    const form = (e.target as HTMLElement).closest<HTMLFormElement>('#student-batch-form');
    if (form) {
      e.preventDefault();
      const fd = new FormData(form);
      const payload = { name: String(fd.get('name') || '').trim(), email: String(fd.get('email') || '').trim(), phone: String(fd.get('phone') || '').trim() };
      if (!payload.name) { toast('Name is required', 'warn'); return; }
      const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]'); if (btn) btn.disabled = true;
      try {
        await addStudentToBatch({ ...payload, batchId: getSelectedBatchId() });
        toast('Learner added', 'success');
        closeModal('student-batch-add');
        rosters[getSelectedBatchId()] = await listStudentsForBatch(getSelectedBatchId()).catch(() => []);
        (window as any).__rerender?.();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Learner could not be added', 'warn');
        if (btn) btn.disabled = false;
      }
      return;
    }
    const bf = (e.target as HTMLElement).closest<HTMLFormElement>('#batch-create-form');
    if (bf) {
      e.preventDefault();
      const fd = new FormData(bf);
      const batch = {
        courseId: String(fd.get('courseId') || ''), name: String(fd.get('name') || '').trim(),
        startDate: String(fd.get('startDate') || ''), endDate: String(fd.get('endDate') || ''),
        tutorUid: String(fd.get('tutorUid') || ''), capacity: Number(fd.get('capacity') || 24),
        schedule: String(fd.get('schedule') || '').trim(),
        status: String(fd.get('status') || 'scheduled') as 'draft' | 'scheduled' | 'live' | 'completed' | 'archived',
        materialIds: [],
      };
      if (!batch.name || !batch.courseId || !batch.startDate || !batch.endDate || !batch.schedule) { toast('Complete the required batch fields', 'warn'); return; }
      const btn = bf.querySelector<HTMLButtonElement>('button[type="submit"]'); if (btn) btn.disabled = true;
      try {
        if (isProductionData()) await createBatch(batch);
        toast('Batch saved', 'success');
        closeModal('batch-create');
        productionBatchesLoaded = false;
        productionBatches = null;
        await refreshBatches(true);
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Batch could not be saved', 'warn');
        if (btn) btn.disabled = false;
      }
    }
  });
}

function openAddStudentForm(): void {
  openModal({
    id: 'student-batch-add',
    title: 'Add learner to batch',
    subtitle: 'Adds this person to the batch roster.',
    width: 480,
    html: '<form id="student-batch-form">' +
      '<div class="stack-tight">' +
        '<label class="t-eyebrow">Full name<input required name="name" placeholder="e.g. Anjali Sharma" class="form-input"/></label>' +
        '<label class="t-eyebrow">Email<input required type="email" name="email" placeholder="anjali@example.com" class="form-input"/></label>' +
        '<label class="t-eyebrow">Phone<input required name="phone" placeholder="+91 98XXX XXXXX" class="form-input"/></label>' +
      '</div>' +
      '<div class="row" style="margin-top:20px;gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-batch-close>Cancel</button>' +
        '<button class="btn btn-primary" type="submit">Add to this batch</button>' +
      '</div>' +
    '</form>',
  });
}

function openBatchForm(): void {
  const courseOptions = courseCache.map((c) => '<option value="' + c.id + '">' + escapeHtml(c.title) + '</option>').join('');
  openModal({
    id: 'batch-create',
    title: 'New batch',
    subtitle: 'Dates, tutor and cohort boundary.',
    width: 560,
    html: '<form id="batch-create-form">' +
      '<div class="stack-tight">' +
        '<label class="t-eyebrow">Batch name<input required name="name" placeholder="e.g. Sep 02 · Web" class="form-input"/></label>' +
        '<label class="t-eyebrow">Course<select required name="courseId" class="form-input"><option value="">Select course</option>' + courseOptions + '</select></label>' +
        '<div class="row" style="gap:12px">' +
          '<label style="flex:1" class="t-eyebrow">Start<input required type="date" name="startDate" class="form-input"/></label>' +
          '<label style="flex:1" class="t-eyebrow">End<input required type="date" name="endDate" class="form-input"/></label>' +
        '</div>' +
        '<label class="t-eyebrow">Tutor UID<input name="tutorUid" class="form-input" placeholder="Firebase Auth UID"/></label>' +
        '<div class="row" style="gap:12px">' +
          '<label style="flex:1" class="t-eyebrow">Capacity<input type="number" name="capacity" value="24" class="form-input"/></label>' +
          '<label style="flex:1" class="t-eyebrow">Status<select name="status" class="form-input"><option value="draft">Draft</option><option value="scheduled" selected>Scheduled</option><option value="live">Live</option><option value="completed">Completed</option></select></label>' +
        '</div>' +
        '<label class="t-eyebrow">Schedule<input required name="schedule" placeholder="Tue/Thu · 18:30–20:30" class="form-input"/></label>' +
      '</div>' +
      '<div class="row" style="margin-top:24px;gap:10px;justify-content:flex-end">' +
        '<button class="btn btn-ghost" type="button" data-batch-close>Cancel</button>' +
        '<button class="btn btn-primary" type="submit">Save batch</button>' +
      '</div>' +
    '</form>',
  }, (panel) => {
    const sel = panel.querySelector<HTMLSelectElement>('#batch-create-form select[name="courseId"]');
    if (sel && sel.options.length <= 1) {
      void listCourses().then((cs) => {
        cs.forEach((c) => { const o = document.createElement('option'); o.value = c.id; o.textContent = c.title; sel.appendChild(o); });
        if (sel.options.length <= 1) {
          const o = document.createElement('option'); o.value = ''; o.textContent = 'No courses yet — create one in Courses first'; sel.appendChild(o);
        }
      });
    }
  });
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
